import { SignInButton, useAuth } from '@clerk/react'
import { AlertCircle, Cloud, Loader2, Zap } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from '#router'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { primeSubmitSound } from '@/components/user-profile/code-editor-settings'
import { useUserSettings } from '@/components/user-settings-context'
import { useEditorActions } from '../editor-actions'
import { type SaveStatus, useSessionOptional } from '../session-context'

export type EditorCursorPosition = {
  lineNumber: number
  column: number
} | null

interface CodeFooterProps {
  cursorPosition: EditorCursorPosition
}

interface SaveIndicatorProps {
  status: SaveStatus
}

function SaveIndicator({ status }: SaveIndicatorProps) {
  // Map status to display config
  // - 'idle' and 'saved' → "Saved" (green)
  // - 'pending' and 'saving' → "Saving" (amber)
  // - 'restored' → "Restored from Cloud" (blue)
  // - 'error' → "Save failed" (red)
  const config = {
    idle: {
      label: 'Saved',
      className: 'text-emerald-600 dark:text-emerald-400',
      tooltip: 'All changes saved to cloud',
      showSpinner: false,
    },
    saved: {
      label: 'Saved',
      className: 'text-emerald-600 dark:text-emerald-400',
      tooltip: 'All changes saved to cloud',
      showSpinner: false,
    },
    pending: {
      label: 'Saving...',
      className: 'text-amber-600 dark:text-amber-400',
      tooltip: 'Saving your changes...',
      showSpinner: true,
    },
    saving: {
      label: 'Saving...',
      className: 'text-amber-600 dark:text-amber-400',
      tooltip: 'Saving your changes...',
      showSpinner: true,
    },
    restored: {
      label: 'Restored from Cloud',
      className: 'text-blue-600 dark:text-blue-400',
      tooltip: 'Your previous code has been restored',
      showSpinner: false,
    },
    error: {
      label: 'Save failed',
      className: 'text-red-600 dark:text-red-400',
      tooltip: 'Your latest change could not be saved to cloud',
      showSpinner: false,
    },
  } as const

  const { label, className, tooltip, showSpinner } = config[status]

  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span className={`flex items-center gap-1.5 ${className}`}>
          {status === 'error' ? (
            <AlertCircle className="h-3.5 w-3.5" />
          ) : showSpinner ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Cloud className="h-3.5 w-3.5" />
          )}
          <span className="text-xs font-medium">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent variant="panel">{tooltip}</TooltipContent>
    </Tooltip>
  )
}

export function CodeFooter({ cursorPosition }: CodeFooterProps) {
  const { isSignedIn } = useAuth()
  const location = useLocation()
  const currentPathWithSearch = `${location.pathname}${location.search}`
  const { run, submit, isExecuting, isSubmitting } = useEditorActions()
  const sessionContext = useSessionOptional()
  const { settings } = useUserSettings()

  // Prime audio in the user gesture context so iOS allows playback later
  const handleSubmit = useCallback(() => {
    primeSubmitSound(settings.editor.successSubmitSound)
    submit()
  }, [submit, settings.editor.successSubmitSound])

  const isBusy = isExecuting || isSubmitting
  const saveStatus = sessionContext?.saveStatus ?? 'idle'

  // Detect platform on client-side only to avoid hydration mismatch
  // Start with null (not yet detected) and update after mount
  const [isMac, setIsMac] = useState<boolean | null>(null)

  useEffect(() => {
    // Only detect platform on client side after mount
    setIsMac(
      typeof navigator !== 'undefined' &&
        /Mac|iPod|iPhone|iPad/.test(navigator.platform),
    )
  }, [])

  // Keyboard shortcut labels based on platform
  // Returns null during SSR to avoid hydration mismatch
  const shortcuts = useMemo(() => {
    if (isMac === null) return null // Not yet detected (SSR)
    return {
      run: isMac ? "⌘'" : "Ctrl+'",
      submit: isMac ? '⌘↵' : 'Ctrl+Enter',
    }
  }, [isMac])

  if (!isSignedIn) {
    return (
      <footer className="border-border/60 flex items-center border-t bg-blue-600 px-4 py-2.5 text-sm text-white">
        <span>You need to&nbsp;</span>
        <SignInButton
          mode="modal"
          forceRedirectUrl={currentPathWithSearch}
          fallbackRedirectUrl={currentPathWithSearch}
        >
          <button
            type="button"
            className="font-medium text-white underline underline-offset-2 hover:text-blue-200"
          >
            log in / sign up
          </button>
        </SignInButton>
        <span>&nbsp;to run or submit</span>
      </footer>
    )
  }

  return (
    <footer className="border-border/60 flex items-center justify-between gap-2 border-t px-3 py-2">
      <div className="flex items-center gap-3">
        <p className="text-muted-foreground text-xs">
          {cursorPosition
            ? `Ln ${cursorPosition.lineNumber}, Col ${cursorPosition.column}`
            : 'Ln –, Col –'}
        </p>
        <SaveIndicator status={saveStatus} />
      </div>
      <nav
        aria-label="Code execution actions"
        className="flex items-center gap-2"
      >
        {isBusy ? (
          <Button
            type="button"
            size="default"
            disabled
            aria-label={isSubmitting ? 'Submitting code...' : 'Running code...'}
            className="min-w-[140px] cursor-not-allowed bg-amber-600 text-white hover:bg-amber-600 dark:bg-amber-600 dark:text-white dark:hover:bg-amber-600"
          >
            <Zap className="mr-2 h-4 w-4" />
            {isSubmitting ? 'Judging...' : 'Running...'}
          </Button>
        ) : (
          <>
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="secondary"
                  size="default"
                  onClick={run}
                  aria-label="Run code against visible test cases"
                  className="dark:text-foreground dark:bg-neutral-700 dark:hover:bg-neutral-600"
                >
                  Run
                </Button>
              </TooltipTrigger>
              <TooltipContent variant="panel">
                <span>Run against visible test cases</span>
                {shortcuts && (
                  <kbd className="text-muted-foreground ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-xs dark:bg-neutral-800">
                    {shortcuts.run}
                  </kbd>
                )}
              </TooltipContent>
            </Tooltip>
            <Tooltip delayDuration={150}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  size="default"
                  onClick={handleSubmit}
                  aria-label="Submit code for full evaluation"
                  className="bg-[#5BB366] text-white hover:bg-[#4fa95c] dark:bg-[#5BB366] dark:text-white dark:hover:bg-[#4fa95c]"
                >
                  Submit
                </Button>
              </TooltipTrigger>
              <TooltipContent variant="panel">
                <span>Submit for full evaluation</span>
                {shortcuts && (
                  <kbd className="text-muted-foreground ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-xs dark:bg-neutral-800">
                    {shortcuts.submit}
                  </kbd>
                )}
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </nav>
    </footer>
  )
}
